/**
 * Logger utility with environment-based log levels
 * Controls console output based on current environment
 */

type LogLevelT = 'debug' | 'info' | 'warn' | 'error' | 'none';

type LoggerConfigT = {
  level: LogLevelT;
  prefix?: string;
  enabled: boolean;
};

const LOG_LEVELS: Record<LogLevelT, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4
};

/**
 * Create a logger instance with specific configuration
 */
export const createLogger = (namespace: string, config?: Partial<LoggerConfigT>) => {
  const isDevelopment = import.meta.env.DEV;
  const isDebugMode = import.meta.env.VITE_DEBUG_MODE === 'true';
  
  // Default configuration
  const defaultConfig: LoggerConfigT = {
    level: isDevelopment || isDebugMode ? 'debug' : 'error',
    prefix: `[${namespace}]`,
    enabled: true
  };
  
  const loggerConfig = { ...defaultConfig, ...config };
  
  const shouldLog = (level: LogLevelT): boolean => {
    if (!loggerConfig.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[loggerConfig.level];
  };
  
  const formatMessage = (message: string): string => {
    return loggerConfig.prefix ? `${loggerConfig.prefix} ${message}` : message;
  };
  
  return {
    debug: (message: string, ...args: any[]) => {
      if (shouldLog('debug')) {
        console.log(formatMessage(message), ...args);
      }
    },
    
    info: (message: string, ...args: any[]) => {
      if (shouldLog('info')) {
        console.log(formatMessage(message), ...args);
      }
    },
    
    warn: (message: string, ...args: any[]) => {
      if (shouldLog('warn')) {
        console.warn(formatMessage(message), ...args);
      }
    },
    
    error: (message: string, ...args: any[]) => {
      if (shouldLog('error')) {
        console.error(formatMessage(message), ...args);
      }
    },
    
    setLevel: (level: LogLevelT) => {
      loggerConfig.level = level;
    },
    
    enable: () => {
      loggerConfig.enabled = true;
    },
    
    disable: () => {
      loggerConfig.enabled = false;
    }
  };
};

/**
 * Global logger instance for general use
 */
export const logger = createLogger('App');